import { Observable } from 'rxjs';

import {
    CoachingType,
    Opportunity as ExtOpportunity,
    GoalStatus,
    OpportunityStatus,
} from '../../../coaching-base/general';
import {
    adjustOpportunity,
    determineOpportunityType,
    determineOpportunityTypeByComponent,
    opportunityToViewTransformer,
    OpportunityType,
    OpportunityView,
    overrideCoachTypeByComponent,
    patchOpportunity,
    sortOpportunities,
} from '../../../coaching-base/opportunity';
import { GoalEventDataPoint } from '../../../coaching-commitment/models/goal-event-data-point.model';
import { ModelTransformer } from '../../../common/common.model';
import { MetricDataItem } from '../../../insight/performance-data/metric.pd.model';
import { PeriodType } from '../../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { determineStatusLabel } from '../opportunity-view.transformer';

import { OaPeopleSort, OpportunitySort, SortDirection } from './sorting.model';

// default display for inactive/greyed out performers
export const SHOW_INACTIVE_PERFORMERS: boolean = true;

export enum ACTIVE_TAB_VIEW {
    SELF = 'SELF',
    TEAM = 'TEAM',
    OTHERS = 'OTHERS',
}

export enum OaPopulationStatus {
    LEFT_EARLY,
    STARTED_LATE,
    INCLUDED_IN_OA,
    UNACCOUNTED,
}

export enum CoachingInsightSessionKeys {
    SHOW_ACTIVE_PERFORMER = 'coachingInsight-showActivePerformer',
    FILTER_OPTIONS = 'coachingInsight-filterOptions',
    COLUMN_CONFIG_ITEMS = 'coachingInsight-columnConfigItems',
    COACHINGINSIGHT_USERID = 'coachingInsight-userId',
}

// ------------------------------------------------------------

export interface OAFilter {
    filterMetrics: Metric[];
    opportunityType: any;
    coachingState: any;
    filterSort: any;
}
export interface ElasticTabConfig {
    liclass?: any;
    text?: string;
    activeConditionComparator?: any;
    clickHandler?: Function;
    clickHandlerParams?: any[];
    sideTemplateRef?: any;
    sideContext?: any;
    index?: number;
    enabled?: boolean; // if clickable
    isDisabled?: Function;
}

// Dedicated Metric interface
export interface Metric {
    value?: string; // because ng-select requires value to always be string
    label?: string;
    disabled?: boolean;

    metricId?: string;
    metricInitials?: string;
    metricShortName?: string;
    metricName?: string;
    oaThresholdValue?: number;
    rankOrder?: number;
}

// TODO rename to OpportunityView
export interface Opportunity extends ExtOpportunity {
    value?: string;
    label?: string;

    metricIdString?: string;
    goalExpired?: boolean;

    /**
     * @deprecated
     * this and view.oppType must be kept in sync.
     * this property is secondary to view.oppType.
     * all assignment updates should happen to view.oppType first
     */
    opportunityType?: OpportunityType;

    /** @deprecated use coachTypeId instead */
    coachingType?: CoachingType; // is it Coached, Uncoached, Missed, Non-OA

    components?: Opportunity[];
    coachedComponents?: Opportunity[];
    primaryMetric?: Metric;
    metric?: Metric;
    activeComponentId?: number;
    componentMetric?: Metric;
    commitmentStrings?: string[];
    commitments?: Commitment[];
    interventionRemaining?: number;

    /** @deprecated use oppStatusId instead */
    oppStatus?: OpportunityStatus;

    goalStatusLabel?: string;

    /**
     * used in goal-score.component, oa-legend.component
     * TODO merge into root before renaming root to OpportunityView
     */
    view?: OpportunityView;
}

// -------------------------------------------------- PERFORMANCE DETAIL INTERFACES --------------------------------------------------

export interface GraphLabel {
    componentColour?: string;
    goalColour?: string;
    targetColour?: string;
    text?: string;
    targetText?: string;
    goalText?: string;
    interactive?: boolean;
}

export interface MetricDetailPerformanceData {
    period?: { startDate: Date; endDate: Date };
    data?: PerformanceData[];
}
export interface MetricDetailGridObservables {
    period?: { startDate: Date; endDate: Date };
    observables?: Array<Observable<any>>;
}
export interface Behaviour {
    behaviourId: number;
    behaviourName: string;
}

export interface RawGraphData {
    mainData?: MetricDataItem[];
    targetData?: any[];
    sgAvgData?: any[];
    goalData?: any[];
    eventData?: GoalEventDataPoint[];
    [prop: string]: any;
}

export enum GraphType {
    VARIANCE = 'variance',
    TREND = 'trend',
    GRID = 'grid',
}

export interface Commitment {
    commitmentId?: number;
    behaviour?: string;
    subbehaviour?: string;
    coachId?: number;
    coachName?: string;
    sessionTypeId?: number;
    sessionTypeName?: string;
    sessionDate?: Date;
    duration?: number;
    statusId?: number;
    statusName?: string;
    status?: string;
    createdOn?: Date;
    lastUpdated?: Date;
    observationCount?: number;
    evaluationCount?: number;
    goalCount?: number;
    consistency?: number;
    avgproficiency?: number;
    goalId?: number;
    metricId?: number;
    opportunities?: number[];
    goals?: number[];
    userId?: number;

    details?: CommitmentDetails;

    activeOAGoalCount?: number;
    activeNonOAGoalCount?: number;
}

export interface CommitmentListEntry {
    numOfNonoaGoals?: number;
    numOfOAGoals?: number;
    numOfObservations?: number;

    commitmentId?: number;
    behaviour?: string;
    subbehaviour?: string;
    statusId?: number;
    statusName?: string;
    status?: string;
    userId?: number;
    metricIds?: number[];
    isNonKpi?: boolean;
}

export interface ExtCommitment {
    numOfNonoaGoals?: number;
    numOfOAGoals?: number;
    numOfObservations?: number;

    commitmentId?: number;
    behaviour?: string;
    subbehaviour?: string;
    statusId?: number;
    statusName?: string;
    status?: string;
    userId?: number;
    isNonKpi?: number;
    metricId?: number;
}

export interface CommitmentDetails {
    goals?: any[];
    opportunities?: Opportunity[];
    observations?: any[];
    commitment?: Commitment;
}

export interface Goal {
    statusName?: string;
    statusId?: number;
    currentPerformanceGoalStatusId?: number;
    startDate?: string;
    endDate?: string;
    goalValue?: number;
    goalPercent?: number;
    actual?: number;
    thresholdColour?: string;
    metricId?: number;
    userId?: number;
    goalId?: number;
    isOpportunity?: number;

    // calculated
    percentToGoal?: number;
    isPending?: boolean;
}

export interface PerformanceData {
    goalPercent?: number;
    targetPercent?: number;
    actual?: number;
    eventDate?: string;
    gap?: number;
    goal?: number;
    goalThresholdColour?: string;
    goalThresholdName?: string;
    metricID?: number;
    metricInitials?: string;
    metricName?: string;
    metricShortName?: string;
    performanceViewTypeId?: number;
    previousActual?: number;
    primaryId?: number;
    primaryInitials?: string;
    primaryName?: string;
    primaryShortName?: string;
    primaryType?: number;
    projectedActual?: number;
    projectedPctToTgt?: number;
    projectedTarget?: number;
    rankOrder?: number;
    secondaryId?: number;
    secondaryInitials?: string;
    secondaryName?: string;
    secondaryShortName?: string;
    secondaryType?: number;
    target?: number;
    targetThresholdColour?: string;
    targetThresholdName?: string;
    trend?: string;
    trendName?: string;
    arrow?: string;
    arrowColour?: string;
    opportunityThreshold?: number;
}

export interface OaPopulation {
    team: OaPerson[];
    others: OaPerson[];
    self: OaPerson;
}

export class OaPerson {
    userId?: number;
    firstName?: string;
    lastName?: string;
    supergroupId?: number;
    supergroupName?: string;
    supergroupAge?: number;
    managerId?: number;
    managerName?: string;
    managerAge?: number;
    roleName?: string;
    roleAge?: number;
    oaPopulation?: string;
    startDate?: Date;
    endDate?: Date;
    startedLate?: boolean;
    leftEarly?: boolean;
    includedInOa?: boolean;
    oaScore?: number;
    isInHierarchy?: boolean;
    coachedOpportunities?: number;
    expectedOpportunities?: number;
    coachedNonOpportunities?: number;
    totalOpportunities?: number;
    nonKPICoaching?: number;
    percentOfPeriod?: number;
    oldestOpportunity?: number;
    biggestOpportunity?: number;
    oaThreshold?: string;

    oaPopulationStatus?: OaPopulationStatus; // calculated
    opportunities?: Opportunity[];
    nonoa?: Opportunity[];
    coached?: Opportunity[];
    uncoached?: Opportunity[];
    missed?: Opportunity[];
    isCurrentlyReporting?: boolean; // true if leftEarly == false

    active?: boolean;
    displayForTeam?: boolean; // toggleable to show if still part of team or not

    allcommitments?: CommitmentListEntry[];
    nonKpiCommitments?: CommitmentListEntry[];

    public display = true; // decides of row of oaperson is displayed
    public hasBeenInit = false;
    public extradataloaded = false;

    public toggle(forceval: boolean = undefined): void {
        // eslint-disable-next-line no-unneeded-ternary
        this.active =
            forceval !== undefined
                ? forceval
                : // eslint-disable-next-line no-unneeded-ternary
                this.active === true
                ? false
                : true;
    }

    constructor(init?: Partial<OaPerson>) {
        Object.assign(this, init);

        const t = Number(this.userId);
        const x = Number(this.oaScore);

        if (typeof t !== 'number' || typeof x !== 'number') {
            console.error('Invalid OaPerson.', this);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public distributeOpportunities() {
        this.coached = [];
        this.uncoached = [];
        this.nonoa = [];
        this.missed = [];

        this.opportunities.forEach((d: Opportunity) => {
            switch (d.coachingType) {
                case CoachingType.COACHED:
                    this.coached.push(d);
                    break;
                case CoachingType.UNCOACHED:
                    this.uncoached.push(d);
                    break;
                case CoachingType.MISSED:
                    this.missed.push(d);
                    break;
                case CoachingType.NON_OA:
                    this.nonoa.push(d);
                    break;
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public defaultMetricSort(sortorder: SortDirection = SortDirection.DESC) {
        this.defaultCoachedSort(sortorder);
        this.defaultUncoachedSort();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public getSortedOpportunitiesByTarget(
        sortorder: SortDirection = SortDirection.DESC,
    ) {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const sortPctTarget = (a, b) => {
            const at = a.percentToTarget;
            const bt = b.percentToTarget;

            let comp = 0;

            if (at == null) return 1;
            else if (bt == null) return -1;
            else if (at < bt) {
                comp = 1;
            } else if (at > bt) {
                comp = -1;
            }

            return sortorder === SortDirection.ASC ? comp * -1 : comp;
        };

        this.coached.sort(sortPctTarget);
        this.uncoached.sort(sortPctTarget);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public getSortedOpportunitiesByGoal(
        sortorder: SortDirection = SortDirection.DESC,
    ) {
        this.coached.sort((a, b) => {
            const at = a.percentToGoal;
            const bt = b.percentToGoal;

            let comp = 0;

            if (at == null) return 1;
            else if (bt == null) return -1;
            else if (at < bt) {
                comp = 1;
            } else if (at > bt) {
                comp = -1;
            }

            return sortorder === SortDirection.ASC ? comp * -1 : comp;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public defaultCoachedSort(
        sortorder: SortDirection = SortDirection.DESC,
        sortByTarget = false,
    ) {
        if (this.coached) {
            this.coached.sort((a, b) => sortOpportunities(a, b, 'asc'));
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public defaultUncoachedSort() {
        if (this.uncoached) {
            this.uncoached.sort((a, b) => sortOpportunities(a, b, 'asc'));
        }
    }

    // can sort by percent to goal / percent to target
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public coachedSort(
        sortorder: SortDirection = SortDirection.DESC,
        sortByTarget = false,
    ) {
        if (this.coached) {
            if (!sortByTarget) {
                this.coached.sort(
                    OpportunitySort.sortByPercentToGoal(sortorder),
                );
            } else {
                this.coached.sort(
                    OpportunitySort.sortByPercentToTarget(sortorder),
                );
            }
        }
    }

    // only sorts by percent to target
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public uncoachedSort(sortorder: SortDirection = SortDirection.DESC) {
        if (this.uncoached) {
            this.uncoached.sort(
                OpportunitySort.sortByPercentToTarget(sortorder),
            );
        }
    }

    // gets the required information based on the current sort
    public getTopCoachedGoal(sortdirection: SortDirection): number {
        if (this.coached.length) {
            this.getSortedOpportunitiesByGoal(sortdirection);
            return this.coached[0].percentToGoal;
        }
        return null;
    }

    public getTopTarget(sortdirection: SortDirection): number {
        this.getSortedOpportunitiesByTarget(sortdirection);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const topcoached =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.coached && this.coached.length
                ? this.coached[0].percentToTarget
                : null;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const topuncoached =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.uncoached && this.uncoached.length
                ? this.uncoached[0].percentToTarget
                : null;

        if (topcoached == null && topuncoached != null) {
            return topuncoached;
        } else if (topuncoached == null && topcoached != null) {
            return topcoached;
        } else if (sortdirection === SortDirection.DESC) {
            return topcoached < topuncoached ? topuncoached : topcoached;
        } else if (sortdirection === SortDirection.ASC) {
            return topcoached < topuncoached ? topcoached : topuncoached;
        }
        // after sorting, get top target
        return 0;
    }

    public mapCommitmentsToOpportunity(
        m: Opportunity,
        commitments: CommitmentListEntry[],
    ): Opportunity {
        m.commitments = commitments.filter((c: CommitmentListEntry) => {
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            return (
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                c.metricIds.indexOf(m.metricId) > -1 &&
                m.userId === c.userId &&
                (m.goalStatusId === GoalStatus.ACTIVE ||
                    m.goalStatusId === GoalStatus.PENDING_CLOSE ||
                    m.goalStatusId === GoalStatus.CLOSED) &&
                (m.coachingType === CoachingType.COACHED ||
                    m.coachingType === CoachingType.NON_OA)
            );
        });

        return m;
    }
}

// EXTERNAL  --------------------------------------------------

export class ExtOaPerson {
    userId?: number;
    firstName?: string;
    lastName?: string;
    supergroupId?: number;
    supergroupName?: string;
    supergroupAge?: number;
    managerId?: number;
    managerName?: string;
    managerAge?: number;
    roleName?: string;
    roleAge?: number;
    oaPopulation?: string;
    startDate?: Date;
    endDate?: Date;
    startedLate?: number;
    leftEarly?: number;
    includedInOa?: number;
    oaScore?: number;
    isInHierarchy?: number;
    coachedOpportunities?: number;
    expectedOpportunities?: number;
    coachedNonOpportunities?: number;
    totalOpportunities?: number;
    nonKPICoaching?: number;
    percentOfPeriod?: number;
    oldestOpportunity?: number;
    biggestOpportunity?: number;
    oaThreshold?: string;
}

export interface OaResponse {
    oaPeople: ExtOaPerson[];
    opportunities: ExtOpportunity[];
    commitments: ExtCommitment[];
    metrics: Metric[];
}
export class OaPersonTransformer
    implements ModelTransformer<OaPerson[], OaResponse>
{
    transform(model: OaResponse): OaPerson[] {
        const people = model.oaPeople;
        const extcommitments: ExtCommitment[] = model.commitments;
        const _opportunities = model.opportunities;
        let commitments: CommitmentListEntry[] = [];
        let metrics = model.metrics;
        let opportunities: Opportunity[] = [];

        opportunities = mapOpps(_opportunities);

        metrics = mapMetricsForDropdown(metrics);

        opportunities = mapOppsWithPercentages(opportunities, metrics);

        opportunities = mapOppsWithComponents(opportunities);
        commitments = mapOppsWithCommitments(commitments, extcommitments); // map opps with commitments first, the resulting will be processed for their components

        return processOaPeople(people, opportunities, commitments);
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function mapOppsWithPerformer(performer: OaPerson, opps: Opportunity[]) {
    let opportunities: Opportunity[] = [];
    opportunities = mapOpps(opps);
    opportunities = mapOppsWithComponents(opportunities);
    performer.active = true;

    opportunities.forEach((s) => {
        s.metric = {
            metricId: s.metricId,
            metricName: s.metricName,
            metricShortName: s.metricShortName,
        } as any;

        s.label = s.metricName;
    });
    return processOaPeople([performer], opportunities, []);
}

const mapOppsWithComponents = (opps: Opportunity[]): Opportunity[] => {
    const oppscopy = [...opps]; // take components from

    // OPPS WITH COMPONENTS (no parentMetricId)
    opps = opps.filter((g) => {
        return g.metricId && g.parentMetricId === null;
    });

    return opps.map((m: Opportunity) => {
        // get Opportunities that has a MetricId = ComponentMetricId of the current Opportunity
        if (isAPrimaryOpportunity(m)) {
            // the parentMetricId is the PARENT'S metricId
            const components = oppscopy.filter(
                (g) => g.parentMetricId === m.metricId && g.userId === m.userId,
            );
            m.components = components;
            m.coachedComponents = [];

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (m.components && m.components.length) {
                m.components.sort((a, b) => sortOpportunities(a, b, 'asc'));
                m.coachedComponents = m.components.filter(
                    (c: Opportunity) =>
                        c.goalStatusId === GoalStatus.ACTIVE ||
                        c.coachingType === CoachingType.COACHED,
                );
                m.coachedComponents.sort(
                    OpportunitySort.sortByEstGoalCompletionDate(
                        SortDirection.DESC,
                    ),
                );
            }

            if (m.coachedComponents.length) {
                // const activeComponent = m.coachedComponents.find(f => f.coachingType === CoachingType.COACHED);
                // const sortedcomponents = m.coachedComponents.sort((a: Opportunity, b: Opportunity) => a.estCompletionForGoal < b.estCompletionForGoal ? 1 : -1);
                // const activeComponent = sortedcomponents[0];
                const activeComponent = m.coachedComponents[0];

                if (activeComponent) {
                    // if this parent has a Component metric that has a goal but no opportunity (Non-OA component metric)
                    m.coachTypeId = overrideCoachTypeByComponent(
                        m,
                        activeComponent,
                        m.view.oppType,
                    );
                    m.coachingType = m.coachTypeId;

                    if (m.opportunityType !== OpportunityType.ACTIVE) {
                        m.activeComponentId = activeComponent.metricId;
                        m.percentToGoal = activeComponent.percentToGoal;
                        m.goalStatusLabel = determineStatusLabel(
                            activeComponent.goalStatusId,
                            activeComponent.approved === false,
                            activeComponent.actual,
                            activeComponent.percentToGoal,
                            activeComponent.coachingType,
                        );
                        m.goalStatusId = activeComponent.goalStatusId;
                        m.disposition = activeComponent.disposition;
                        m.estCompletionForGoal =
                            activeComponent.estCompletionForGoal;
                    }

                    const a = new Date(m.estCompletionForGoal || 0);
                    const b = new Date(
                        activeComponent.estCompletionForGoal || 0,
                    );
                    m.estCompletionForGoal =
                        a.getTime() > b.getTime()
                            ? m.estCompletionForGoal
                            : activeComponent.estCompletionForGoal;

                    m.view.oppType = determineOpportunityTypeByComponent(
                        m,
                        activeComponent,
                        m.view.oppType,
                    );
                    m.opportunityType = m.view.oppType;

                    adjustOpportunity(m, m.view.oppType, m.view);
                }

                m.coachedComponents.forEach((h) => {
                    h.commitmentStrings.forEach((g) => {
                        // eslint-disable-next-line @typescript-eslint/prefer-includes
                        if (m.commitmentStrings.indexOf(g) === -1) {
                            m.commitmentStrings.push(g);
                        }
                    });
                });
            }

            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);
            const d = new Date(m.estCompletionForGoal || 0);
            m.goalExpired = currentDate.getTime() > d.getTime();
        }

        return m;
    });
};

const mapOppsWithCommitments = (
    comms: CommitmentListEntry[],
    extcomms: ExtCommitment[],
): CommitmentListEntry[] => {
    extcomms.forEach((c: ExtCommitment) => {
        const h = comms.find(
            (g: CommitmentListEntry) => g.commitmentId === c.commitmentId,
        );

        if (!h) {
            const cc: CommitmentListEntry = {
                numOfNonoaGoals: c.numOfNonoaGoals,
                numOfOAGoals: c.numOfOAGoals,
                numOfObservations: c.numOfObservations,
                commitmentId: c.commitmentId,
                behaviour: c.behaviour,
                subbehaviour: c.subbehaviour,
                statusId: c.statusId,
                statusName: c.statusName,
                status: c.status,
                userId: c.userId,
                isNonKpi: c.isNonKpi === 1,
            };

            if (c.metricId !== null) {
                cc.metricIds = [c.metricId];
            } else {
                cc.metricIds = [];
            }

            comms.push(cc);
        } else {
            if (!h.metricIds) {
                h.metricIds = [];
            }
            h.metricIds.push(c.metricId);
        }
    });

    return comms;
};

const mapOppsWithPercentages = (
    opps: Opportunity[],
    metrics: Metric[],
): Opportunity[] => {
    return opps.map((o: Opportunity) => {
        // regardless of if this opportunity is a primary or component, always get its associated metric
        o.metric = metrics.find(
            (f: Metric) => f.metricId === o.metricId.toString(),
        );

        o.metricIdString = o.metricId.toString();
        o.value = o.metric.metricId.toString();
        o.label = o.metric.metricShortName;
        return o;
    });
};

const mapMetricsForDropdown = (metrics: Metric[]): Metric[] => {
    return metrics.map((m) => {
        m.rankOrder = m.rankOrder ? 1 : -1;

        m.label = m.metricName;
        m.metricId = m.metricId != null ? m.metricId.toString() : null;
        m.value = m.metricId;
        return m;
    });
};
const mapOpps = (
    _opportunities: Array<ExtOpportunity | Opportunity>,
): Opportunity[] => {
    const opportunities: Opportunity[] = [];

    _opportunities.forEach((o: ExtOpportunity | Opportunity) => {
        const f: Opportunity = {
            userId: o.userId,
            metricId: o.metricId,
            metricIdString: o.metricId.toString(),
            metricName: o.metricName,
            metricShortName: o.metricShortName,
            parentMetricId: o.parentMetricId,
            isOppActive: o.isOppActive,
            rankScore: o.rankScore,
            actual: o.actual,
            percentToTarget: o.percentToTarget,
            percentToGoal: o.percentToGoal,
            percentToIntervention: o.percentToIntervention,
            targetThresholdColour: o.targetThresholdColour, // target threshold colour
            goalThresholdColour: o.goalThresholdColour, // goal threshold colour
            goalStatusLabel: determineStatusLabel(
                o.goalStatusId,
                o.approved === false,
                o.actual,
                o.percentToGoal,
                o.coachTypeId,
            ),
            goalStatusId: o.goalStatusId,
            oaAging: o.oaAging,
            oaThresholdValue: o.oaThresholdValue,
            openDays: o.openDays,
            disposition: o.disposition,
            isExpired: o.isExpired,
            estCompletionForGoal: o.estCompletionForGoal,
            daysInIntervention: o.daysInIntervention,
            oppStatusName: o.oppStatusName,
            oppStatusId: o.oppStatusId,
            oppStatus: OpportunityStatus[OpportunityStatus[o.oppStatusId]],
            coachTypeId: o.coachTypeId,
            coachingType: CoachingType[CoachingType[o.coachTypeId]],
            commitmentStrings: o.commitmentName
                ? o.commitmentName.split('\n')
                : [],
            goalEndDate: new Date(o.goalEndDate),
            approved: o.approved,
            approvalId: o.approvalId,
            approvalTargetId: o.approvalTargetId,
            approvalFromUserId: o.approvalFromUserId,
            approvalUpdatedOn: new Date(o.approvalUpdatedOn),
            view: opportunityToViewTransformer([o], _opportunities, false)[0],
        };

        if (f.commitmentStrings) {
            f.commitmentStrings = f.commitmentStrings.map((g) => g.trim());
        }

        patchOpportunity(f);

        if (f.daysInIntervention !== null && f.percentToIntervention !== null) {
            f.interventionRemaining =
                Math.round(
                    f.daysInIntervention * (100 / f.percentToIntervention),
                ) - f.daysInIntervention;
        }

        f.view.oppType = determineOpportunityType(f);
        f.opportunityType = f.view.oppType;

        adjustOpportunity(f, f.view.oppType, f.view);

        opportunities.push(f);
    });

    return opportunities;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const processOaPeople = (
    people: Array<ExtOaPerson | OaPerson>,
    opportunities: Opportunity[],
    commitments: Commitment[],
) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const oaPeople: OaPerson[] = [];

    people = people.map((p) => {
        p.oaPopulation = p.oaPopulation
            ? ACTIVE_TAB_VIEW[p.oaPopulation.toUpperCase()]
            : ACTIVE_TAB_VIEW.OTHERS;
        return p;
    });

    const myself = people.find((f) => f.oaPopulation === ACTIVE_TAB_VIEW.SELF);
    // if (myself) {
    //     people = people.filter(p =>
    //         p.oaPopulation === ACTIVE_TAB_VIEW.SELF ||
    //         // only include performers that are still in OA
    //         p.includedInOa === 1 ||
    //         p.managerId === myself.userId ||
    //         ((new Date(p.endDate).setHours(0, 0, 0, 0) === CoachingDateToday.setHours(0, 0, 0, 0)) && !p.includedInOa)
    //     );
    // }

    const x = people.map(function (
        extteamoa: ExtOaPerson | OaPerson,
    ): OaPerson {
        let oascore = extteamoa.oaScore;

        oascore = oascore != null ? Math.round(oascore * 100) : null;

        if (oascore > 100) {
            oascore = 100;
        }

        const item = new OaPerson({
            ...extteamoa,
            userId: extteamoa.userId,
            firstName: extteamoa.firstName,
            lastName: extteamoa.lastName,
            supergroupId: extteamoa.supergroupId,
            supergroupName: extteamoa.supergroupName,
            supergroupAge: extteamoa.supergroupAge,
            managerId: extteamoa.managerId,
            managerName: extteamoa.managerName,
            managerAge: extteamoa.managerAge,
            roleName: extteamoa.roleName,
            roleAge: extteamoa.roleAge,
            oaPopulation: extteamoa.oaPopulation,
            startDate: extteamoa.startDate,
            endDate: extteamoa.endDate,
            startedLate: extteamoa.startedLate === 1,
            leftEarly: extteamoa.leftEarly === 1,
            includedInOa: extteamoa.includedInOa === 1,
            isInHierarchy: extteamoa.isInHierarchy === 1,
            coachedOpportunities: extteamoa.coachedOpportunities,
            expectedOpportunities: extteamoa.expectedOpportunities,
            coachedNonOpportunities: extteamoa.coachedNonOpportunities,
            totalOpportunities: extteamoa.totalOpportunities,
            nonKPICoaching: extteamoa.nonKPICoaching,
            percentOfPeriod: extteamoa.percentOfPeriod,
            oldestOpportunity: extteamoa.oldestOpportunity,
            biggestOpportunity: extteamoa.biggestOpportunity,
            oaThreshold: extteamoa.oaThreshold,
            oaScore: oascore,
            active: false,
            displayForTeam: true, // initially always display everyone in team
        });

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (myself && myself.userId !== null) {
            item.isCurrentlyReporting =
                item.leftEarly !== true && myself.userId === item.managerId;

            if (
                !item.isCurrentlyReporting &&
                item.oaPopulation === ACTIVE_TAB_VIEW[ACTIVE_TAB_VIEW.TEAM]
            ) {
                item.displayForTeam = SHOW_INACTIVE_PERFORMERS;
            }
        } else {
            item.displayForTeam = SHOW_INACTIVE_PERFORMERS;
        }

        item.oaScore = item.oaScore != null ? oascore : null;

        // get all primary metrics for this user
        item.opportunities = opportunities.filter(
            (m) => m.userId === item.userId && isAPrimaryOpportunity(m),
        );

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (item.opportunities && item.opportunities.length) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const tempopps = [...item.opportunities];

            item.opportunities = item.opportunities.map((m: Opportunity) => {
                m = item.mapCommitmentsToOpportunity(m, commitments);

                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                if (m.components && m.components.length) {
                    m.components.forEach((comp) => {
                        comp = item.mapCommitmentsToOpportunity(
                            comp,
                            commitments,
                        );
                    });
                }

                return m;
            });
        }

        item.distributeOpportunities();
        item.defaultMetricSort(SortDirection.DESC);

        item.allcommitments = commitments
            .filter((c) => {
                return (
                    c.userId === item.userId && c.statusId !== GoalStatus.DRAFT
                );
            })
            .sort((a, b) => {
                return a.commitmentId > b.commitmentId ? -1 : 1;
            });

        // get commitments that have no goal
        item.nonKpiCommitments = item.allcommitments.filter(
            (c: CommitmentListEntry) => c.isNonKpi,
        );
        item.nonKPICoaching = item.nonKpiCommitments.length;

        return item;
    });

    x.sort(OaPeopleSort.sortByRequirements());

    // sort the performers by below
    return [
        ...x.filter(
            (f) => f.oaPopulation.toUpperCase() === ACTIVE_TAB_VIEW.TEAM,
        ),
        ...x.filter(
            (f) => f.oaPopulation.toUpperCase() === ACTIVE_TAB_VIEW.OTHERS,
        ),
        ...x.filter(
            (f) => f.oaPopulation.toUpperCase() === ACTIVE_TAB_VIEW.SELF,
        ),
    ];
};

/* TODO remove. unused */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const targetSelectionCriteria = [
    (p, metricId) => metricId === p.metricId && !p.isInitialValues,
    (p, metricId) => metricId === p.metricId && p.isInitialValues,
];

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const isAPrimaryOpportunity = (o: Opportunity) => {
    // true primary OA              // NON-OA opp
    return o.metricId != null && o.parentMetricId == null;
};

export const COACHING_WIDGET_PERIOD_TYPES = [
    {
        label: null,
        value: PeriodType.MONTH,
        tlKey: TranslationKeyMap._7439_MTD,
    },
    {
        label: null,
        value: PeriodType.ROLLING_30,
        tlKey: TranslationKeyMap._7445__30_DAY_ROLLING,
    },
];

export function targetVariancePercent(
    actual: number,
    target: number,
    rankOrder: number,
): number {
    if (target === null) return null;
    if (rankOrder === 0) rankOrder = -1;

    const t = target === 0 ? 0.00001 : target;

    let v = ((actual - t) / t) * rankOrder + 1;

    v *= 100;

    if (v < 0) {
        return 0;
    } else if (v > 999) {
        return 999;
    }

    return v;
}
export function goalVariancePercent(
    actual: number,
    goal: number,
    rankOrder: number,
): number {
    if (goal == null || goal === 0) return null;
    if (rankOrder === 0) rankOrder = -1;
    let v = ((actual - goal) / goal) * rankOrder + 1;

    v *= 100;

    if (v < 0) {
        return 0;
    }

    return v;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function utcDateStringNoTime(dateval: string | Date) {
    let mm = null;
    let dd = null;
    let yy = null;
    let d = null;

    if (typeof dateval === 'string') {
        d = new Date(dateval);
    } else {
        d = dateval;
    }

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    mm = d.getUTCMonth() + 1;
    dd = d.getUTCDate();
    yy = d.getUTCFullYear();

    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `${yy}-${pad(mm, 2)}-${pad(dd, 2)}`;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function pad(n, width, z?) {
    z = z || '0';
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    n = `${n}`;
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}
